import React, {useState, useRef, useCallback} from 'react';
import {Button, Icon} from '@gravity-ui/uikit';
import {Plus} from '@gravity-ui/icons';
import {useToaster} from '@gravity-ui/uikit';
import {PreparedImageData} from '../../lib';
import {ImageUploadModal} from '../ImageUploadModal';
import {ImageType} from '../../model';
import styles from './ImageUploadButton.module.scss';
import classNames from 'classnames';

interface ImageUploadButtonProps {
  fundraiserId?: string;
  imageType?: ImageType;
  onUploadSuccess?: () => void;
  isCreating?: boolean;
  aspectRatio?: number; // Соотношение сторон для crop
  onPrepareImageData?: (data: PreparedImageData, previewUrl: string) => void;
  className?: string;
  withBg?: boolean;
}

export const ImageUploadButton = ({
  imageType = ImageType.MAIN,
  fundraiserId,
  isCreating = false,
  aspectRatio,
  onUploadSuccess,
  onPrepareImageData,
  className,
  withBg = false,
}: ImageUploadButtonProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {add} = useToaster();

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (!file.type.startsWith('image/')) {
        add({
          name: 'image-upload-error',
          title: 'Ошибка загрузки изображения',
          content: 'Пожалуйста, выберите файл изображения.',
          theme: 'danger',
        });
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const previewUrl = reader.result as string;
        setSelectedFile(file);
        setPreviewUrl(previewUrl);
        setIsModalOpen(true);
      };
      reader.readAsDataURL(file);
      event.target.value = '';
    }
  };

  const handleUploadSuccess = useCallback(() => {
    setIsModalOpen(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    if (onUploadSuccess) {
      onUploadSuccess();
    }
  }, [onUploadSuccess]);

  const handlePrepareImageData = useCallback(
    (data: PreparedImageData, preview: string) => {
      if (onPrepareImageData) {
        onPrepareImageData(data, preview);
      }
    },
    [onPrepareImageData],
  );

  return (
    <>
      <div className={classNames(className, {[styles.imageUploadButtonWithBg]: withBg})}>
        <Button view="normal" size="xl" onClick={handleButtonClick}>
          <Icon data={Plus} size={20} />
        </Button>
      </div>

      <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" style={{display: 'none'}} />

      <ImageUploadModal
        open={isModalOpen}
        setOpen={setIsModalOpen}
        file={selectedFile}
        previewUrl={previewUrl}
        onUploadSuccess={handleUploadSuccess}
        fundraiserId={fundraiserId}
        imageType={imageType}
        isCreating={isCreating}
        onPrepareImageData={handlePrepareImageData}
        aspectRatio={aspectRatio}
      />
    </>
  );
};
